import React, { useCallback, useMemo } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { setUser } from './userSlice';
import axios from 'axios';
import { createBrowserRouter, RouterProvider, redirect, Outlet, Navigate } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorPage from './ErrorPage';
import { Login } from './login/Login';
import Dashboard from './dashboard/Dashboard';
import './index.css';
import Users from './users/Users';
import Publications from './publications/Publications';
import ArchivedPublications from './publications/archived/ArchivedPublications';
import './service/axiosConfig';
import { SsoLogin } from './login/SsoLogin';
import DashboardSso from './dashboardsso/DashboardSso';
import DashboardAdmin from './dashboardadmin/AdminComponents/DashboardAdmin';
import Reviews from './reviews/Reviews';
import ShareReviewPage from './reviews/share/ShareReviewPage';
import { Option } from './types/option';
import { selectIsAuthenticated, selectIsAdmin } from './selectors/userSelectors';
import { Categories } from './categories/Categories';
import Administration from './administration/Administration';
import { ArchiveCategories } from './categories/ArchiveCategories';
import { PublicationForm } from './publications/form/PublicationForm';
import { store } from './store';
import { mapMeResponseToUserState } from './service/authService';

const AppWithUserPersistence = () => {
    const dispatch = useDispatch();
    const authentication = useCallback(async () => {
        const token = Option.some(localStorage.getItem('bearer'));

        await token.match(
            async () => {
                throw redirect('/login');
            },
            async v => {
                try {
                    const response = await axios.get('/api/me', {
                        headers: { Authorization: `Bearer ${v}` },
                    });
                    if (response.status !== 200) {
                        throw redirect('/login');
                    }

                    const userState = mapMeResponseToUserState(
                        response.data,
                        localStorage.getItem('sso') === 'true',
                    );

                    if (userState === null) {
                        throw redirect('/login');
                    }

                    dispatch(setUser(userState));
                } catch (error) {
                    console.error(error);
                    throw redirect('/login');
                }
            },
        );

        return null;
    }, [dispatch]);

    if (localStorage.getItem('countUnreadReview')) {
        localStorage.removeItem('countUnreadReview');
    }

    const router = useMemo(
        () =>
            createBrowserRouter([
                {
                    path: '/',
                    element: <App />,
                    errorElement: <ErrorPage />,
                    children: [
                        { path: 'login', element: <Login /> },
                        { path: 'sso-login', element: <SsoLogin /> },
                        {
                            element: <UserRoute />,
                            loader: authentication,
                            shouldRevalidate: () => true,
                            children: [
                                {
                                    path: 'dashboard',
                                    element: <Dashboard />,
                                },
                                {
                                    path: 'dashboard_agency',
                                    element: <DashboardSso />,
                                },
                                {
                                    path: 'dashboard_admin',
                                    element: <DashboardAdmin />,
                                },
                                {
                                    path: 'users',
                                    element: <Users />,
                                },
                                {
                                    path: 'publications',
                                    element: <Publications />,
                                },
                                {
                                    path: 'add_publication',
                                    element: <PublicationForm />,
                                },
                                {
                                    path: `edit_publication/:publicationId`,
                                    element: <PublicationForm />,
                                },
                                {
                                    path: `show_publication/:publicationId`,
                                    element: <PublicationForm />,
                                },
                                {
                                    path: 'archived_publications',
                                    element: <ArchivedPublications />,
                                },
                                {
                                    path: 'reviews',
                                    element: <Reviews />,
                                },
                                {
                                    path: 'share-review/:reviewId',
                                    element: <ShareReviewPage />,
                                },
                            ],
                        },
                        {
                            path: 'administration',
                            loader: authentication,
                            shouldRevalidate: () => true,
                            element: (
                                <PrivateRoute>
                                    <Administration />
                                </PrivateRoute>
                            ),
                        },
                        {
                            path: 'categories',
                            loader: authentication,
                            shouldRevalidate: () => true,
                            element: (
                                <PrivateRoute>
                                    <Categories />
                                </PrivateRoute>
                            ),
                        },
                        {
                            path: 'archived_categories',
                            loader: authentication,
                            shouldRevalidate: () => true,
                            element: (
                                <PrivateRoute>
                                    <ArchiveCategories />
                                </PrivateRoute>
                            ),
                        },
                    ],
                },
            ]),
        [authentication],
    );

    return <RouterProvider router={router} />;
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <AppWithUserPersistence />
        </Provider>
    </React.StrictMode>,
);

function UserRoute() {
    const isAuthenticated = useSelector(selectIsAuthenticated);

    return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
    return useSelector(selectIsAdmin) ? <>{children}</> : <ErrorPage />;
}
